import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, CalendarCheck } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";
import { nav } from "@/data/site";
import { Logo } from "@/components/Logo";
import { GoldButton } from "@/components/ui-kit";

export const Header = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [pathname]);

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
        scrolled ? "bg-ink/85 backdrop-blur-xl border-b border-white/10" : "bg-transparent"
      }`}
    >
      <div className="max-w-[1400px] mx-auto px-5 lg:px-10 h-20 flex items-center justify-between">
        <Link to="/" data-testid="header-logo-link" aria-label="Body Evolution 2.0 home">
          <Logo light className="scale-90 origin-left" />
        </Link>

        <nav className="hidden xl:flex items-center gap-6">
          {nav.map((n) => (
            <Link
              key={n.to}
              to={n.to}
              data-testid={`nav-${n.to === "/" ? "home" : n.to.slice(1)}`}
              className={`text-[13px] tracking-wide font-medium transition-colors ${
                pathname === n.to ? "text-gold" : "text-ivory/70 hover:text-ivory"
              }`}
            >
              {n.label}
            </Link>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <GoldButton to="/contatti" data-testid="header-cta-button" className="hidden sm:inline-flex !px-5 !py-2.5 text-[13px]">
            <CalendarCheck size={16} /> Prenota una prova gratuita
          </GoldButton>
          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setOpen((v) => !v)}
            className="xl:hidden text-ivory p-2"
            aria-label="Apri menu"
          >
            {open ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
            className="xl:hidden bg-ink/95 backdrop-blur-xl border-t border-white/10 overflow-hidden"
          >
            <div className="px-5 py-6 flex flex-col">
              {nav.map((n) => (
                <Link
                  key={n.to}
                  to={n.to}
                  data-testid={`mobile-nav-${n.to === "/" ? "home" : n.to.slice(1)}`}
                  className={`py-3 border-b border-white/5 text-sm tracking-wide ${
                    pathname === n.to ? "text-gold" : "text-ivory/80"
                  }`}
                >
                  {n.label}
                </Link>
              ))}
              <GoldButton to="/contatti" className="mt-5 w-full">
                <CalendarCheck size={16} /> Prenota una prova gratuita
              </GoldButton>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};
